import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useBrand } from '@/hooks/useBrand';
import { TYPE_COMPTE_CLIENT } from '@/lib/accountType';
import RedirectCountdown from './components/RedirectCountdown';

type PaymentFlow = 'abonnement' | 'achat';
type VerifyState = 'idle' | 'checking' | 'paid' | 'pending' | 'failed';

const AUTO_REDIRECT_SECONDS = 12;

/** Normalise le paramètre `type` / `flow` de l'URL. */
function normalizeFlow(raw: string): PaymentFlow | null {
  const value = raw.trim().toLowerCase();
  if (['abonnement', 'abonnements', 'subscription', 'subscribe', 'sub', 'plan'].includes(value)) return 'abonnement';
  if (['achat', 'achats', 'commande', 'commandes', 'order', 'orders', 'shop', 'boutique', 'panier', 'cart'].includes(value)) return 'achat';
  return null;
}

function formatAmount(value: string): string {
  const num = Number(String(value).replace(/[^\d.,-]/g, '').replace(',', '.'));
  if (!Number.isFinite(num) || num <= 0) return '';
  return num.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

export default function PaymentReturnPage() {
  const { user } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const reference = (searchParams.get('reference') || searchParams.get('ref') || searchParams.get('transaction_id') || '').trim();
  const rawStatus = (searchParams.get('status') || searchParams.get('state') || '').trim().toLowerCase();
  const amountLabel = formatAmount(searchParams.get('amount') || searchParams.get('montant') || '');
  const currencyLabel = (searchParams.get('currency') || searchParams.get('devise') || '').trim().toUpperCase();
  const explicitRedirect = (searchParams.get('redirect') || '').trim();

  const [verify, setVerify] = useState<VerifyState>('idle');
  const [seconds, setSeconds] = useState(AUTO_REDIRECT_SECONDS);
  const [redirectCancelled, setRedirectCancelled] = useState(false);

  /** Parcours déduit de l'URL, de la référence, puis du type de compte. */
  const flow: PaymentFlow = useMemo(() => {
    const fromUrl = normalizeFlow(
      searchParams.get('type') || searchParams.get('flow') || searchParams.get('destination') || '',
    );
    if (fromUrl) return fromUrl;
    if (/^SHOP-/i.test(reference)) return 'achat';
    if (/^ZIFSUB-/i.test(reference)) return 'abonnement';
    if (user && Number(user.typecompte) === TYPE_COMPTE_CLIENT) return 'achat';
    return 'abonnement';
  }, [reference, searchParams, user]);

  const merchantPath = '/dashboard/abonnement';
  const clientPath = '/mon-compte?tab=orders';

  const targetPath = explicitRedirect.startsWith('/')
    ? explicitRedirect
    : flow === 'achat'
      ? clientPath
      : merchantPath;
  const targetLabel = flow === 'achat' ? 'Voir mes achats' : 'Voir mes abonnements';
  const altPath = flow === 'achat' ? merchantPath : clientPath;
  const altLabel = flow === 'achat' ? 'Espace commerçant — mes abonnements' : 'Espace client — mes achats';

  // ── Vérification réelle du paiement (jamais la parole du navigateur) ──
  useEffect(() => {
    let cancelled = false;
    const isSubscription = /ZIFSUB-/i.test(reference) || flow === 'abonnement';

    const run = async () => {
      if (rawStatus === 'failed' || rawStatus === 'cancelled' || rawStatus === 'canceled') {
        setVerify('failed');
        return;
      }
      if (!reference || !isSubscription) {
        setVerify('idle');
        return;
      }
      setVerify('checking');
      const subMatch = reference.match(/ZIFSUB-(\d+)/i);
      const subscriptionId = subMatch ? Number(subMatch[1]) : undefined;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const { data, error } = await supabase.functions.invoke('feexpay-status', {
            body: { reference, subscriptionId },
          });
          if (error) throw error;
          const status = String((data as { status?: string } | null)?.status || '').toLowerCase();
          if (status === 'paid') {
            if (!cancelled) setVerify('paid');
            return;
          }
          if (status === 'failed') {
            if (!cancelled) setVerify('failed');
            return;
          }
        } catch {
          /* on retente */
        }
        await new Promise((resolve) => { window.setTimeout(resolve, 2200); });
        if (cancelled) return;
      }
      if (!cancelled) setVerify('pending');
    };

    void run();
    return () => { cancelled = true; };
  }, [flow, rawStatus, reference]);

  // ── Décompte de redirection automatique ──
  const shouldRedirect = !redirectCancelled && verify !== 'checking' && verify !== 'failed';

  useEffect(() => {
    if (!shouldRedirect) return;
    if (seconds <= 0) {
      navigate(targetPath, { replace: true });
      return;
    }
    const timer = window.setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [navigate, seconds, shouldRedirect, targetPath]);

  const cancelRedirect = useCallback(() => {
    setRedirectCancelled(true);
    setSeconds(0);
  }, []);

  useEffect(() => {
    document.title = verify === 'failed' ? 'Paiement non abouti — Zifek' : 'Confirmation de paiement — Zifek';
  }, [verify]);

  const paidByGateway = rawStatus === 'success' || rawStatus === 'successful' || rawStatus === 'paid' || rawStatus === 'completed';
  const isSuccess = verify === 'paid' || (verify === 'idle' && paidByGateway);
  const isFailure = verify === 'failed';
  const isChecking = verify === 'checking';

  const tone = isFailure
    ? { bg: 'bg-red-50', ring: 'bg-red-100', icon: 'ri-close-line', color: 'text-red-600', label: 'Paiement non abouti' }
    : isSuccess
      ? { bg: 'bg-accent-50', ring: 'bg-accent-100', icon: 'ri-check-line', color: 'text-accent-600', label: 'Paiement confirmé' }
      : { bg: 'bg-secondary-50', ring: 'bg-secondary-100', icon: 'ri-time-line', color: 'text-secondary-700', label: isChecking ? 'Vérification en cours' : 'Paiement enregistré' };

  const title = isFailure
    ? 'Le paiement n’a pas abouti'
    : isSuccess
      ? 'Merci, votre paiement est confirmé !'
      : isChecking
        ? 'Vérification de votre paiement…'
        : 'Votre paiement est en cours de confirmation';

  const description = isFailure
    ? 'La transaction a été refusée ou annulée par l’opérateur. Aucun montant n’a été débité sur votre compte. Vous pouvez relancer le paiement à tout moment.'
    : isSuccess
      ? flow === 'achat'
        ? 'Votre commande est bien enregistrée. Vous retrouvez son suivi, son reçu et vos factures dans « Mes achats ».'
        : 'Votre abonnement est actif. Votre nouvelle formule et sa prochaine échéance sont visibles dans « Mes abonnements ».'
      : isChecking
        ? 'Nous interrogeons FeexPay pour confirmer la transaction. Cela prend quelques secondes, ne fermez pas cette page.'
        : 'Nous n’avons pas encore reçu la confirmation de FeexPay. Elle arrive généralement en quelques minutes : votre espace se mettra à jour automatiquement.';

  return (
    <div className="relative min-h-screen bg-background-50 flex flex-col">
      <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-background-100 to-transparent pointer-events-none"></div>

      <header className="relative w-full px-4 md:px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 no-underline">
          <span className="w-9 h-9 rounded-lg bg-foreground-900 text-background-50 flex items-center justify-center">
            <i className="ri-store-3-line text-base"></i>
          </span>
          <span className="text-sm font-bold font-heading text-foreground-950 tracking-tight">
            {brand?.name || 'Zifek'}
          </span>
        </Link>
        <Link
          to="/"
          className="text-xs font-medium text-foreground-500 no-underline hover:text-foreground-800 transition-colors whitespace-nowrap"
        >
          Retour à l’accueil
        </Link>
      </header>

      <main className="relative flex-1 w-full px-4 md:px-6 py-6 md:py-10">
        <div className="mx-auto w-full max-w-xl">
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
            <div className="flex flex-col items-center text-center">
              <div className={`w-16 h-16 rounded-full ${tone.ring} flex items-center justify-center mb-4`}>
                <i className={`${isChecking ? 'ri-loader-4-line animate-spin' : tone.icon} text-3xl ${tone.color}`}></i>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${tone.bg} ${tone.color} mb-3`}>
                <i className="ri-shield-check-line"></i>
                {tone.label}
              </span>
              <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-2">{title}</h1>
              <p className="text-sm text-foreground-500 leading-relaxed max-w-md">{description}</p>
            </div>

            {(amountLabel || reference) && (
              <div className="mt-6 rounded-lg border border-background-200/70 bg-background-100/60 divide-y divide-background-200/70 text-sm">
                {amountLabel && (
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="text-foreground-500 text-xs">Montant</span>
                    <span className="font-bold text-foreground-950 whitespace-nowrap">
                      {amountLabel} {currencyLabel}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <span className="text-foreground-500 text-xs">Parcours</span>
                  <span className="font-medium text-foreground-800 whitespace-nowrap">
                    {flow === 'achat' ? 'Achat boutique' : 'Abonnement'}
                  </span>
                </div>
                {reference && (
                  <div className="flex items-start justify-between gap-3 px-4 py-2.5">
                    <span className="text-foreground-500 text-xs whitespace-nowrap">Référence</span>
                    <span className="font-mono text-[11px] text-foreground-800 break-all text-right">{reference}</span>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6">
              {isFailure ? (
                <div className="space-y-3">
                  <Link
                    to={flow === 'achat' ? '/panier' : '/dashboard/abonnement/choisir'}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-bold no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-refresh-line"></i>
                    Réessayer le paiement
                  </Link>
                  <Link
                    to={flow === 'achat' ? clientPath : merchantPath}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium no-underline hover:bg-background-200/70 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-go-back-line"></i>
                    {targetLabel}
                  </Link>
                </div>
              ) : isChecking ? (
                <div className="flex items-center justify-center gap-2 text-xs text-foreground-500 py-3">
                  <i className="ri-loader-4-line animate-spin text-primary-500"></i>
                  Confirmation auprès de FeexPay…
                </div>
              ) : (
                <RedirectCountdown
                  targetLabel={targetLabel}
                  targetPath={targetPath}
                  altLabel={altLabel}
                  altPath={altPath}
                  seconds={shouldRedirect ? seconds : 0}
                  onCancel={cancelRedirect}
                />
              )}
            </div>

            {!user && (
              <p className="mt-5 text-[11px] text-foreground-400 text-center leading-relaxed">
                Vous n’êtes pas connecté : la connexion vous sera demandée pour accéder à votre espace.
                {' '}
                <Link to="/login" className="text-foreground-700 underline underline-offset-2 no-underline">Commerçant</Link>
                {' · '}
                <Link to="/login-client" className="text-foreground-700 underline underline-offset-2 no-underline">Client</Link>
              </p>
            )}
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { icon: 'ri-mail-check-line', title: 'Reçu par email', text: 'La confirmation est envoyée à votre adresse.' },
              { icon: 'ri-shield-keyhole-line', title: 'Paiement sécurisé', text: 'Transaction vérifiée directement chez FeexPay.' },
              { icon: 'ri-customer-service-2-line', title: 'Besoin d’aide ?', text: 'Notre support répond sous 24 h ouvrées.' },
            ].map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200/70 bg-background-50 p-3">
                <div className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center mb-2">
                  <i className={`${item.icon} text-sm`}></i>
                </div>
                <p className="text-xs font-semibold text-foreground-900">{item.title}</p>
                <p className="text-[11px] text-foreground-500 mt-0.5 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer className="relative w-full px-4 md:px-6 py-5 border-t border-background-200/70">
        <p className="text-[11px] text-foreground-400 text-center">
          {brand?.name || 'Zifek'} — paiement traité par FeexPay. Cette page confirme votre transaction, elle ne débite rien.
        </p>
      </footer>
    </div>
  );
}